import { noteEditorAtom } from '@/atoms/noteEditorAtoms';
import { simpleMarkdownToHtml } from '@/helpers/markdownToHtml';
import {
  ensureCurrentUserSnippets,
  getCurrentUserSnippets,
} from '@/hooks/query/useQueryUserSnippets';
import { typeSnippetText } from '@/tauri/dictation/api';
import { logger } from '@/utils/logger/logger';
import { isEditorLive } from '@heidi/editor/core';
import { type Editor } from '@tiptap/core';
// Type-only: loads @tiptap/markdown's Editor augmentation (editor.markdown).
import type {} from '@tiptap/markdown';
import { getDefaultStore } from 'jotai';

const snippetContent = async (snippetId: string): Promise<string | null> => {
  const cached = getCurrentUserSnippets();
  const snippets = cached?.snippets ?? (await ensureCurrentUserSnippets()).snippets;
  const content = snippets.find((snippet) => snippet.id === snippetId)?.content;
  return content ? content : null;
};

const insertSnippetContent = (editor: Editor, content: string): void => {
  // Only the markdown-native editor registers a markdown manager. Its snippets
  // must go through that boundary (as its slash menu does), never the legacy
  // HTML serializer, or a `# ` section arrives as a plain heading.
  if (editor.markdown) {
    editor.commands.insertContent(content, { contentType: 'markdown' });
    return;
  }
  editor.commands.insertContent(
    content.includes('\n') ? simpleMarkdownToHtml({ markdown: content }) : content
  );
};

const insertIntoFocusedEditor = (content: string): boolean => {
  const editor = getDefaultStore().get(noteEditorAtom);
  if (!isEditorLive(editor, 'mapped-actions/insert-snippet') || !editor.isFocused) {
    return false;
  }
  // insertContent throws with the snippet body in the message. Callers fire
  // this as a floating promise, and RUM beforeSend does not scrub that text.
  try {
    insertSnippetContent(editor, content);
  } catch (error) {
    logger.error('mapped snippet insert failed', {
      errorName: error instanceof Error ? error.name : 'unknown',
    });
    // A throwing command never dispatches, so nothing landed in the note and
    // typing the text is the fallback.
    return false;
  }
  return true;
};

export type InsertSnippetPorts = {
  getSnippetContent: (snippetId: string) => Promise<string | null>;
  insertIntoFocusedEditor: (content: string) => boolean;
  typeIntoFrontmostApp: (content: string) => Promise<void>;
};

const defaultPorts = (): InsertSnippetPorts => ({
  getSnippetContent: snippetContent,
  insertIntoFocusedEditor,
  typeIntoFrontmostApp: typeSnippetText,
});

export const handleInsertSnippet = async (
  snippetId: string,
  ports: InsertSnippetPorts = defaultPorts()
): Promise<void> => {
  const content = await ports.getSnippetContent(snippetId);
  if (!content) return;
  if (ports.insertIntoFocusedEditor(content)) return;
  try {
    await ports.typeIntoFrontmostApp(content);
  } catch (error) {
    // The native error string can include the characters that failed to type.
    logger.error('mapped snippet type failed', {
      errorName: error instanceof Error ? error.name : typeof error,
    });
  }
};
